import React, { useState, useEffect } from 'react';
import { employeesApi, organizationApi } from '../services/api';
import { ClockIcon, CalendarIcon, UserGroupIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { formatDateInTimezone, formatTimeInTimezone, getDateAndTimeInTimezone } from '../utils/dateUtils';

interface TimeReportEntry {
  employee_id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
  total_entries: number;
  total_minutes: number;
  first_clock_in?: string;
  last_clock_out?: string;
}

interface TimeEntry {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
  clock_in: string;
  clock_out?: string;
  duration_minutes?: number;
  notes?: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
}

interface CurrentlyWorking {
  time_entry_id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
  clock_in: string;
  minutes_worked: number;
}

export const TimeReport: React.FC = () => {
  const [report, setReport] = useState<TimeReportEntry[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [currentlyWorking, setCurrentlyWorking] = useState<CurrentlyWorking[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const [period, setPeriod] = useState<{ start_date: string; end_date: string }>({
    start_date: '',
    end_date: '',
  });
  
  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [modalData, setModalData] = useState({
    employee_id: '',
    clock_in_date: '',
    clock_in_time: '',
    clock_out_date: '',
    clock_out_time: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');

  const loadReport = async () => {
    setLoading(true);
    try {
      const [reportData, entriesData, employeeData, workingData, orgSettings] = await Promise.all([
        employeesApi.getTimeReport(startDate, endDate),
        employeesApi.getAllTimeEntries({ start_date: startDate, end_date: endDate, limit: 500 }),
        employeesApi.list(),
        employeesApi.getCurrentlyWorking(),
        organizationApi.getSettings(),
      ]);
      setTimezone(orgSettings.timezone || 'America/New_York');
      setReport(reportData.report || []);
      setTimeEntries(entriesData.time_entries || []);
      setEmployees(employeeData.employees || []);
      setCurrentlyWorking(workingData.currently_working || []);
      setPeriod(reportData.period || { start_date: startDate, end_date: endDate });
    } catch (error) {
      console.error('Failed to load time report:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh currently working every 60 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const workingData = await employeesApi.getCurrentlyWorking();
        setCurrentlyWorking(workingData.currently_working || []);
      } catch (error) {
        console.error('Failed to refresh currently working:', error);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatHoursDecimal = (minutes: number) => {
    return (minutes / 60).toFixed(2);
  };

  const totalMinutes = report.reduce((sum, r) => sum + parseInt(r.total_minutes?.toString() || '0'), 0);

  const openAddModal = () => {
    setEditingEntry(null);
    const now = new Date();
    setModalData({
      employee_id: employees[0]?.id || '',
      clock_in_date: now.toISOString().split('T')[0],
      clock_in_time: '09:00',
      clock_out_date: now.toISOString().split('T')[0],
      clock_out_time: '17:00',
      notes: '',
    });
    setShowModal(true);
  };

  const openEditModal = (entry: TimeEntry) => {
    setEditingEntry(entry);
    const clockIn = getDateAndTimeInTimezone(entry.clock_in, timezone);
    const clockOut = entry.clock_out ? getDateAndTimeInTimezone(entry.clock_out, timezone) : null;
    
    setModalData({
      employee_id: entry.employee_id,
      clock_in_date: clockIn.date,
      clock_in_time: clockIn.time,
      clock_out_date: clockOut?.date || '',
      clock_out_time: clockOut?.time || '',
      notes: entry.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const clockIn = `${modalData.clock_in_date}T${modalData.clock_in_time}:00`;
      const clockOut = modalData.clock_out_date && modalData.clock_out_time 
        ? `${modalData.clock_out_date}T${modalData.clock_out_time}:00`
        : undefined;

      if (editingEntry) {
        await employeesApi.updateTimeEntry(editingEntry.id, {
          clock_in: clockIn,
          clock_out: clockOut || null,
          notes: modalData.notes || undefined,
        });
      } else {
        await employeesApi.createTimeEntry({
          employee_id: modalData.employee_id,
          clock_in: clockIn,
          clock_out: clockOut,
          notes: modalData.notes || undefined,
        });
      }
      
      setShowModal(false);
      loadReport();
    } catch (error) {
      console.error('Failed to save time entry:', error);
      alert('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry: TimeEntry) => {
    if (!window.confirm(`Delete time entry for ${entry.first_name} ${entry.last_name} on ${formatDateInTimezone(entry.clock_in, timezone)}?`)) {
      return;
    }
    
    try {
      await employeesApi.deleteTimeEntry(entry.id);
      loadReport();
    } catch (error) {
      console.error('Failed to delete time entry:', error);
      alert('Failed to delete time entry');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Time Report</h1>
          <p className="text-sm text-slate-500 mt-1">Employee hours worked</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover"
          >
            <PlusIcon className="w-4 h-4" />
            Add Time Entry
          </button>
        </div>
      </div>

      {/* Currently Working Section */}
      {currentlyWorking.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <h2 className="text-lg font-semibold text-green-900">
              Currently Clocked In ({currentlyWorking.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentlyWorking.map((worker) => {
              const minutes = Math.round(parseFloat(worker.minutes_worked?.toString() || '0'));
              const hours = Math.floor(minutes / 60);
              const mins = minutes % 60;
              return (
                <div key={worker.time_entry_id} className="bg-white rounded-lg p-3 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">
                        {worker.first_name} {worker.last_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {worker.employee_number || 'No ID'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-700">
                        {hours}h {mins}m
                      </p>
                      <p className="text-xs text-slate-500">
                        since {formatTimeInTimezone(worker.clock_in, timezone)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setViewMode('summary')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            viewMode === 'summary' 
              ? 'bg-brand-blue text-white' 
              : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50'
          }`}
        >
          Summary View
        </button>
        <button
          onClick={() => setViewMode('detailed')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            viewMode === 'detailed' 
              ? 'bg-brand-blue text-white' 
              : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50'
          }`}
        >
          Detailed View
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
          <button
            onClick={loadReport}
            disabled={loading}
            className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Run Report'}
          </button>
          
          {/* Quick date buttons */}
          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date();
                weekAgo.setDate(today.getDate() - 7);
                setStartDate(weekAgo.toISOString().split('T')[0]);
                setEndDate(today.toISOString().split('T')[0]);
              }}
              className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const twoWeeksAgo = new Date();
                twoWeeksAgo.setDate(today.getDate() - 14);
                setStartDate(twoWeeksAgo.toISOString().split('T')[0]);
                setEndDate(today.toISOString().split('T')[0]);
              }}
              className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Last 14 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date();
                monthAgo.setDate(today.getDate() - 30);
                setStartDate(monthAgo.toISOString().split('T')[0]);
                setEndDate(today.toISOString().split('T')[0]);
              }}
              className="px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Last 30 Days
            </button>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Period</p>
              <p className="text-lg font-semibold text-slate-900">
                {period.start_date} to {period.end_date}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ClockIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Hours</p>
              <p className="text-lg font-semibold text-slate-900">
                {formatDuration(totalMinutes)} ({formatHoursDecimal(totalMinutes)} hrs)
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UserGroupIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Employees with Hours</p>
              <p className="text-lg font-semibold text-slate-900">
                {report.filter(r => parseInt(r.total_minutes?.toString() || '0') > 0).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && (
        <>
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-center text-slate-500">
                Loading report...
              </div>
            ) : report.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-center text-slate-500">
                No time entries found for this period
              </div>
            ) : (
              <>
                {report.map((entry) => {
                  const minutes = parseInt(entry.total_minutes?.toString() || '0');
                  return (
                    <div key={entry.employee_id} className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {entry.first_name} {entry.last_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            Employee # {entry.employee_number || '-'}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-slate-500 uppercase">
                          {entry.total_entries} entries
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs uppercase font-semibold text-slate-500">Hours</p>
                          <p className="text-lg font-bold text-slate-900 whitespace-nowrap">
                            {formatDuration(minutes)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase font-semibold text-slate-500">Decimal</p>
                          <p className="text-lg font-semibold text-slate-700 whitespace-nowrap">
                            {formatHoursDecimal(minutes)} hrs
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">Total:</p>
                    <p className="font-bold text-slate-900 whitespace-nowrap">
                      {formatDuration(totalMinutes)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-sm text-slate-600">Decimal</p>
                    <p className="font-bold text-slate-900 whitespace-nowrap">
                      {formatHoursDecimal(totalMinutes)} hrs
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="min-w-[720px] w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Employee #</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Entries</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase whitespace-nowrap">Hours</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase whitespace-nowrap">Decimal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                      Loading report...
                    </td>
                  </tr>
                ) : report.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                      No time entries found for this period
                    </td>
                  </tr>
                ) : (
                  report.map((entry) => {
                    const minutes = parseInt(entry.total_minutes?.toString() || '0');
                    return (
                      <tr key={entry.employee_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">
                            {entry.first_name} {entry.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {entry.employee_number || '-'}
                        </td>
                        <td className="px-6 py-4 text-center text-slate-600">
                          {entry.total_entries}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-slate-900 whitespace-nowrap">
                          {formatDuration(minutes)}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-600 whitespace-nowrap">
                          {formatHoursDecimal(minutes)} hrs
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {report.length > 0 && (
                <tfoot className="bg-slate-50">
                  <tr>
                    <td colSpan={3} className="px-6 py-3 text-right font-semibold text-slate-900">
                      Total:
                    </td>
                    <td className="px-6 py-3 text-right font-bold text-slate-900 whitespace-nowrap">
                      {formatDuration(totalMinutes)}
                    </td>
                    <td className="px-6 py-3 text-right font-bold text-slate-900 whitespace-nowrap">
                      {formatHoursDecimal(totalMinutes)} hrs
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Clock In</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Clock Out</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                    Loading entries...
                  </td>
                </tr>
              ) : timeEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-slate-500">
                    No time entries found for this period
                  </td>
                </tr>
              ) : (
                timeEntries.map((entry) => {
                  const minutes = parseInt(entry.duration_minutes?.toString() || '0');
                  const isOpen = !entry.clock_out;
                  return (
                    <tr key={entry.id} className={`hover:bg-slate-50 ${isOpen ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {entry.first_name} {entry.last_name}
                        </div>
                        <div className="text-xs text-slate-500">{entry.employee_number || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDateInTimezone(entry.clock_in, timezone)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {formatTimeInTimezone(entry.clock_in, timezone)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.clock_out ? (
                          formatTimeInTimezone(entry.clock_out, timezone)
                        ) : (
                          <span className="text-green-600 font-medium">Still working</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">
                        {entry.clock_out ? formatDuration(minutes) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[150px] truncate">
                        {entry.notes || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(entry)}
                            className="p-1 text-slate-400 hover:text-brand-blue"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(entry)}
                            className="p-1 text-slate-400 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Time Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Employee Select */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
                <select
                  value={modalData.employee_id}
                  onChange={(e) => setModalData({ ...modalData, employee_id: e.target.value })}
                  disabled={!!editingEntry}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100"
                >
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} {emp.employee_number ? `(${emp.employee_number})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clock In */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Clock In</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={modalData.clock_in_date}
                    onChange={(e) => setModalData({ ...modalData, clock_in_date: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                  <input
                    type="time"
                    value={modalData.clock_in_time}
                    onChange={(e) => setModalData({ ...modalData, clock_in_time: e.target.value })}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
              </div>

              {/* Clock Out */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Clock Out (optional)</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={modalData.clock_out_date}
                    onChange={(e) => setModalData({ ...modalData, clock_out_date: e.target.value })}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                  <input
                    type="time"
                    value={modalData.clock_out_time}
                    onChange={(e) => setModalData({ ...modalData, clock_out_time: e.target.value })}
                    className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Leave blank if employee is still working</p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={modalData.notes}
                  onChange={(e) => setModalData({ ...modalData, notes: e.target.value })}
                  placeholder="e.g., Forgot to clock in"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !modalData.employee_id || !modalData.clock_in_date || !modalData.clock_in_time}
                className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingEntry ? 'Update' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
